import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, shareReplay, tap, throwError } from 'rxjs';
import { GenericIdbaService } from '../generic-idba-service.service';
import { Acesso } from '../../models/bas/acesso';
import { GenericIdbaRequestModel } from '../../models/generic-idba-request-model';

export interface LocalUsuario {
  codigoEmpresa: number;
  codigoFilial: number;
  nomeFilial: string;
  codigoLocal: number;
  nomeLocal: string;
  localAtual: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AcessoService extends GenericIdbaService {
  declare model: Acesso;

  readonly localUsuarioAtual = signal<{ idUsuario: number; local: LocalUsuario | null } | null>(null);

  private readonly permissoesCache = new Map<string, Observable<any>>();

  constructor(
    http: HttpClient,
    private readonly router: Router,
  ) {
    super(http, new Acesso());
    this.programName = 'bas/acesso/acesso.p';
  }

  pesquisaPermissaoAcesso(): Observable<any> {
    const partesRota = this.router.url.split('?')[0].split('/').filter(Boolean);
    const chave = partesRota.slice(0, 2).join('/') || '/';
    const cache = this.permissoesCache.get(chave);
    if (cache) return cache;

    const request: GenericIdbaRequestModel = new GenericIdbaRequestModel();
    request.program = this.programName;
    request.procedure = 'pi-retorna-permissao-acesso-papel';
    const chamada = this.httpClient
      .post<any>(this.configService.getApiUrl('integrateUrl'), request, { observe: 'response' })
      .pipe(
        catchError((err) => {
          this.permissoesCache.delete(chave);
          return throwError(() => err);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.permissoesCache.set(chave, chamada);
    return chamada;
  }

  limparCachePermissoes(): void {
    this.permissoesCache.clear();
  }

  pesquisaLogAcesso(registro: any) {
    const request: GenericIdbaRequestModel = new GenericIdbaRequestModel();
    request.program = 'bas/acesso/logacesso.p';
    request.procedure = 'pi-listar';
    request.parameters = registro;
    return this.httpClient.post<any>(this.configService.getApiUrl('integrateUrl'), request, {
      observe: 'response',
    });
  }

  buscaLocalUsuario(idUsuario: number) {
    const request = new GenericIdbaRequestModel();
    request.program = this.programName;
    request.procedure = 'pi-retorna-locais-usuario';
    request.parameters = { idUsuario };
    return this.httpClient.post<any>(this.configService.getApiUrl('integrateUrl'), request, {
      observe: 'response',
    }).pipe(tap({
      next: response => {
        const data = response?.body?.data;
        const registros = Array.isArray(data) ? data : data?.registro;
        const locais: LocalUsuario[] = Array.isArray(registros) ? registros : [];
        const local = response.body?.success === false || response.body?.errors?.length
          ? null : locais.find(item => item.localAtual) ?? null;
        this.localUsuarioAtual.set({ idUsuario, local });
      },
      error: () => this.localUsuarioAtual.set(null),
    }));
  }

  alteraLocalUsuario(usuarioId: number, localId: number) {
    const request = new GenericIdbaRequestModel();
    request.program = 'bas/usuario/usuario.p';
    request.procedure = 'pi-altera-local-usuario';
    request.parameters = { usuarioId, localId };
    return this.httpClient.post<any>(this.configService.getApiUrl('integrateUrl'), request, {
      observe: 'response',
    });
  }
}
